// components/ProfileRouteWrapper.js - Fixed version

import React, { useContext, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth/AuthContext';
import ProfilePage from './ProfilePage';
import axios from 'axios';

// API URL - adjust to match your configuration
const API_URL = process.env.REACT_APP_API_URL;

// Component to wrap ProfilePage with security checks
const ProfileRouteWrapper = () => {
  const { isAuthenticated, isLoading, currentUser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Security check for profile access - must be called unconditionally
  useEffect(() => {
    const checkProfileAccess = async () => {
      // Skip checks if in development bypass mode
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_BYPASS_AUTH === 'true') {
        return;
      }
      
      // Skip checks if not authenticated yet
      if (!isAuthenticated || !currentUser) {
        return;
      }
      
      // Get profile ID from URL
      const urlParams = new URLSearchParams(location.search);
      const profileId = urlParams.get('id');
      
      // Skip if no profile ID in URL
      if (!profileId) {
        return;
      }
      
      // Skip check if user is admin
      if (currentUser.role === 'admin') {
        return;
      }
      
      try {
        // Make a server-side check to verify ownership
        const response = await axios.get(
          `${API_URL}/api/profiles/check-access/${profileId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Handle the response from the new API
        const { isOwner, canAccess, profileNotFound, redirectToProfile, message } = response.data;
        
        // If profile not found, redirect to user's own profile
        if (profileNotFound) {
          console.log('Profile not found, redirecting to own profile:', message);
          
          const targetProfileId = redirectToProfile || currentUser.profileId || currentUser.id;
          navigate(`/profiles/profile?id=${targetProfileId}`, { 
            replace: true,
            state: { 
              message: message || 'Profile not found. Redirecting to your profile.',
              error: false
            }
          });
          return;
        }
        
        // If not owner and access denied, redirect to their own profile
        if (!isOwner && !canAccess) {
          console.log('Unauthorized profile access attempt detected:', message);
          
          const targetProfileId = redirectToProfile || currentUser.profileId || currentUser.id;
          navigate(`/profiles/profile?id=${targetProfileId}`, { 
            replace: true,
            state: { 
              message: message || 'You can only access your own profile',
              error: true
            }
          });
        }
      } catch (error) {
        console.error('Error checking profile ownership:', error);
        
        // On error, redirect to user's own profile
        const targetProfileId = currentUser.profileId || currentUser.id;
        if (targetProfileId) {
          navigate(`/profiles/profile?id=${targetProfileId}`, { 
            replace: true,
            state: { 
              message: 'Error accessing profile. Redirecting to your profile.',
              error: true
            }
          });
        } else {
          navigate('/profiles/profile', { replace: true });
        }
      }
    };
    
    checkProfileAccess();
  }, [location.search, currentUser, navigate, isAuthenticated]);

  // Loading state
  if (isLoading) {
    return <div className="loading-container">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>;
  }

  // Authentication check
  if (!isAuthenticated) {
    return <Navigate to="/profiles/login" state={{ from: window.location.pathname }} />;
  }

  // Just return the ProfilePage component - no DashboardContainer
  // The container will be provided by the parent AuthenticatedLayout in App.js
  return <ProfilePage />;
};

export default ProfileRouteWrapper;